import { ApiResponse } from "../ApiResponse";
import { apiUrls } from "../ApiUrls";
import { BaseApiService } from "../BaseApiService";
import {
  StudentRegistrationRequest,
  OrganizationRegistrationRequest,
  MentorRegistrationRequest,
  SponsorRegistrationRequest,
} from "../types/registration.request.types";
import {
  StudentRegistrationResponse,
  OrganizationRegistrationResponse,
} from "../types/registration.response.types";
import { getRefreshToken } from "../../utils/auth";
import { encryptionService } from "../../utils/encryption";

export class AuthService extends BaseApiService {
  /**
   * Login user with email and password
   * Password is encrypted before sending to backend
   */
  static async login(
    email: string,
    password: string,
    role?: string
  ): Promise<ApiResponse<any>> {
    // Encrypt password before sending to backend
    const encryptedPassword = encryptionService.encrypt(password);
    const body: any = { username: email, password: encryptedPassword };
    if (role) body.role = role;
    return this.post(apiUrls.login, body, "Login");
  }

  /**
   * Register a new user
   */
  static async registerStudent(
    userData: StudentRegistrationRequest
  ): Promise<ApiResponse<StudentRegistrationResponse>> {
    const payload: StudentRegistrationRequest = {
      ...userData,
      role: "STUDENT",
      phoneNumber: userData.phoneNumber ?? userData.phone,
      emailOtp: userData.emailOtp ?? "000000",
      phoneOtp: userData.phoneOtp ?? "000000",
    };
    return this.post(apiUrls.registerStudent, payload, "Register Student");
  }

  static async registerOrganization(
    userData: OrganizationRegistrationRequest
  ): Promise<ApiResponse<OrganizationRegistrationResponse>> {
    return this.post(
      apiUrls.registerOrganization,
      userData,
      "Register Organization"
    );
  }

  static async registerMentor(
    userData: MentorRegistrationRequest
  ): Promise<ApiResponse<StudentRegistrationResponse>> {
    userData.role = "MENTOR";
    return this.post(apiUrls.registerMentor, userData, "Register Mentor");
  }

  static async registerSponsor(
    userData: SponsorRegistrationRequest
  ): Promise<ApiResponse<StudentRegistrationResponse>> {
    userData.role = "SPONSOR";
    return this.post(apiUrls.registerSponsor, userData, "Register Sponsor");
  }

  static async refreshToken(): Promise<ApiResponse<any>> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token found");
    }
    return this.post(apiUrls.refreshToken, { refreshToken }, "Refresh Token");
  }

  /**
   * Change user password (requires old password)
   */
  static async changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<ApiResponse<any>> {
    return this.putAuthenticated(
      apiUrls.password.reset,
      { oldPassword, newPassword },
      "Change Password"
    );
  }

  /**
   * Send OTP for email or phone verification
   * requestType: "VERIFY_EMAIL" or "VERIFY_PHONE" or "REGISTER" or "FORGOT_PASSWORD"
   */
  static async sendOtp(
    contactValue: string,
    requestType: "VERIFY_EMAIL" | "VERIFY_PHONE" | "REGISTER" | "FORGOT_PASSWORD",
    role?: string,
    isPhone: boolean = false,
    secondaryContactValue?: string,
    orgName?: string
  ): Promise<ApiResponse<any>> {
    const shouldUsePhone = requestType === "VERIFY_PHONE" || isPhone;
    const payload: any = shouldUsePhone
      ? { phoneNumber: contactValue, requestType }
      : { emailId: contactValue, requestType };
    
    if (role) {
      payload.role = role;
    }

    if (secondaryContactValue) {
      // If primary is phone, secondary is email, and vice versa
      if (shouldUsePhone) {
        payload.emailId = secondaryContactValue;
      } else {
        payload.phoneNumber = secondaryContactValue;
      }
    }

    if (orgName) {
      payload.orgName = orgName;
    }

    console.log('AuthService.sendOtp payload:', payload);

    // For profile contact verification we must be authenticated so that backend
    // can resolve the current user and update their email/phone.
    if (requestType === "VERIFY_EMAIL" || requestType === "VERIFY_PHONE") {
      return this.postAuthenticated(
        apiUrls.password.sendOtp,
        payload,
        "Send OTP"
      );
    }

    // Check for existing token, if not present and it's a forgot password request, use fallback "1234"
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = {};

    if (requestType === "FORGOT_PASSWORD" && !token) {
      headers["Authorization"] = "1234";
    }

    return this.post(apiUrls.password.sendOtp, payload, "Send OTP", headers);
  }

  /**
   * Verify OTP for email or phone
   * requestType: "VERIFY_EMAIL" or "VERIFY_PHONE" or "REGISTER" or "FORGOT_PASSWORD"
   */
  static async verifyOtp(
    contactValue: string,
    otp: string,
    requestType: "VERIFY_EMAIL" | "VERIFY_PHONE" | "REGISTER" | "FORGOT_PASSWORD",
    role?: string,
    isPhone: boolean = false,
    secondaryOtp?: string,
    secondaryContactValue?: string
  ): Promise<ApiResponse<any>> {
    const shouldUsePhone = requestType === "VERIFY_PHONE" || isPhone;
    const payload: any = shouldUsePhone
      ? { phoneNumber: contactValue, otp, requestType }
      : { emailId: contactValue, otp, requestType };
      
    if (role) {
      payload.role = role;
    }
    
    if (secondaryOtp) {
       delete payload.otp;
       
       if (shouldUsePhone) {
         // Primary was phone (so payload has phoneNumber)
         payload.phoneOtp = otp;
         payload.emailOtp = secondaryOtp;
         if (secondaryContactValue) payload.emailId = secondaryContactValue;
       } else {
         // Primary was email (so payload has emailId)
         payload.emailOtp = otp;
         payload.phoneOtp = secondaryOtp;
         if (secondaryContactValue) payload.phoneNumber = secondaryContactValue;
       }
    } else {
        delete payload.otp;
        if (shouldUsePhone) {
            payload.phoneOtp = otp;
        } else {
            payload.emailOtp = otp;
        }
    }

    // Check for existing token, if not present and it's a forgot password request, use fallback "1234"
       if(requestType === "VERIFY_EMAIL" || requestType === "VERIFY_PHONE") {
      return this.postAuthenticated(
        apiUrls.password.verifyOtp,
        payload,
        "Verify OTP"
      );
    }else {
      return this.post(
        apiUrls.password.verifyOtp,
        payload,
        "Verify OTP"
      );
    }
  }

  /**
   * Resend OTP for email or phone verification
   * requestType: "VERIFY_EMAIL" or "VERIFY_PHONE" or "REGISTER" or "FORGOT_PASSWORD"
   */
  static async resendOtp(
    contactValue: string,
    requestType: "VERIFY_EMAIL" | "VERIFY_PHONE" | "REGISTER" | "FORGOT_PASSWORD",
    role?: string,
    isPhone: boolean = false,
    secondaryContactValue?: string,
    orgName?: string
  ): Promise<ApiResponse<any>> {
    const shouldUsePhone = requestType === "VERIFY_PHONE" || isPhone;
    const payload: any = shouldUsePhone
      ? { phoneNumber: contactValue, requestType }
      : { emailId: contactValue, requestType };
      
    if (role) {
      payload.role = role;
    }

    if (secondaryContactValue) {
       if (requestType === "VERIFY_PHONE") {
         payload.emailId = secondaryContactValue;
       } else {
         payload.phoneNumber = secondaryContactValue;
       }
    }

    if (orgName) {
      payload.orgName = orgName;
    }
    
    // Check for existing token, if not present and it's a forgot password request, use fallback "1234"
    const token = localStorage.getItem("token");
    const headers: Record<string, string> = {};
    
    if (requestType === "FORGOT_PASSWORD" && !token) {
      headers["Authorization"] = "1234";
    }

    return this.post(
      apiUrls.password.resendOtp,
      payload,
      "Resend OTP",
      headers
    );
  }

  /**
   * Complete forgot password flow (no authentication required)
   * First verifies the OTP, then completes the password reset
   */
  static async forgotPasswordComplete(
    contactValue: string,
    otp: string,
    newPassword: string,
    isPhone: boolean = false
  ): Promise<ApiResponse<any>> {
    // Step 1: Verify OTP first
    try {
      const verifyResponse = await this.verifyOtp(
        contactValue,
        otp,
        "FORGOT_PASSWORD",
        undefined,
        isPhone
      );

      // If OTP verification fails, throw error
      if (!verifyResponse || verifyResponse.status !== "success") {
        throw new Error(verifyResponse?.message || "OTP verification failed");
      }
    } catch (error: any) {
      // Re-throw verification errors with clear message
      throw new Error(error?.message || "OTP verification failed. Please check your OTP and try again.");
    }

    // Step 2: After successful OTP verification, complete password reset
    // Check for existing token, if not present, use fallback "1234"
    const headers: Record<string, string> = {};

    const payload = isPhone 
      ? { phoneNumber: contactValue, phoneOtp: otp, newPassword, requestType: "FORGOT_PASSWORD" }
      : { emailId: contactValue, emailOtp: otp, newPassword , requestType: "FORGOT_PASSWORD"};

    return this.post(
      apiUrls.password.forgotPasswordComplete,
      payload,
      "Forgot Password Complete",
      headers
    );
  }

  /**
   * Legacy: Forgot password - send OTP to email and reset password
   * @deprecated Use forgotPasswordComplete instead
   */
  static async forgotPassword(
    contactValue: string,
    otp: string,
    newPassword: string,
    role: string,
    isPhone: boolean = false
  ): Promise<ApiResponse<any>> {
    // Redirect to new implementation
    return this.forgotPasswordComplete(contactValue, otp, newPassword, isPhone);
  }

  /**
   * Legacy: Send OTP for forgot password
   * @deprecated Use sendOtp instead
   */
  static async sendForgotPasswordOtp(
    contactValue: string,
    role: string,
    isPhone: boolean = false
  ): Promise<ApiResponse<any>> {
    // Redirect to new implementation
    return this.sendOtp(contactValue, "FORGOT_PASSWORD", role, isPhone);
  }
}
export default AuthService;
